import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  tDicomAbstractName,
  tDicomAbstractDescription,
  tDicomAbstractSecurityNote,
  tDicomTransferSyntaxName,
} from '../../../helpers/i18n/dicomSyntaxI18n';
import {
  HelpCircle,
  Shield,
  Upload,
  Zap,
  Monitor,
  Info,
  FileText,
  Key,
  ChevronDown,
  ChevronRight,
  Server,
  Settings,
  AlertTriangle,
  RefreshCw,
} from 'lucide-react';
import { message } from 'antd';
import { messageI18n, tToast } from '../../../helpers/i18n/toastI18n';
import { CreatableSelect } from './custom-components/creatable-select';
import {
  DicomSyntaxConfig,
  AbstractSyntaxConfig,
  DefaultDicomSyntaxConfig,
  DefaultAbstractSyntaxConfig,
  AeDefaultConfiguration,
  TransferSyntaxValue,
  transferSyntaxInfo,
  DefaultTransferSyntax,
} from './types/interfaces';
import {
  defaultSyntaxConfig,
  defaultDefaultSyntaxConfig,
} from './types/interfaces';
import {
  MANUFACTURER_MODEL_DATA,
  MANUFACTURER_OPTIONS,
  abstractSyntaxInfo,
} from './types/interfaces';
import {
  transformSyntaxConfigToAPI,
  generateAETitle,
  generateAEName,
  Tooltip,
  Button,
  Input,
  Select,
  Textarea,
  Badge,
} from './custom-components/tools';

interface DeviceCreationOptimalFormProps {
  onDeviceCreated?: (device: Record<string, unknown>) => void;
  onCancel?: () => void;
  isModal?: boolean;
  devicesData?: any;
  isLoadingDevices?: boolean;
  address?: any;
  refetchApplicationEntities?: () => Promise<any>;
  refetchDevicesWithConnections?: () => Promise<any>;
}

export default function DeviceCreationOptimalForm({
  onDeviceCreated,
  onCancel,
  isModal = false,
  devicesData,
  isLoadingDevices,
  address,
  refetchApplicationEntities,
  refetchDevicesWithConnections,
}: DeviceCreationOptimalFormProps) {
  const { t } = useTranslation('Organization');
  const AVAILABLE_PORTS = [11103, 11116, 11113, 11114, 11115, 11109];

  const getHostnameOptions = () => {
    const defaultOptions = ['0.0.0.0'];

    if (address?.data?.static_ips && Array.isArray(address.data.static_ips)) {
      const staticIps = address.data.static_ips
        .filter((ip: any) => ip?.address)
        .map((ip: any) => ip.address);

      return staticIps.length > 0 ? staticIps : defaultOptions;
    }

    return defaultOptions;
  };

  const HOSTNAME_OPTIONS = getHostnameOptions();

  const getAllocatedPorts = () => {
    const allocatedPorts = new Set<number>();
    devicesData?.forEach((device: any) => {
      device.connections?.forEach((conn: any) => {
        if (conn.port) {
          allocatedPorts.add(
            typeof conn.port === 'string' ? parseInt(conn.port) : conn.port
          );
        }
      });
    });
    return allocatedPorts;
  };

  const generateConnectionName = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `connection-${year}${month}${day}-${hours}${minutes}`;
  };

  const getFirstAvailablePort = () => {
    const allocatedPorts = getAllocatedPorts();
    const availablePort = AVAILABLE_PORTS.find(
      (port) => !allocatedPorts.has(port)
    );
    return availablePort ? String(availablePort) : '';
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showAeDefaultConfig, setShowAeDefaultConfig] = useState(false);
  // Removed unused expandedSections state
  const [syntaxConfig, setSyntaxConfig] =
    useState<DicomSyntaxConfig>(defaultSyntaxConfig);
  const [expandedAbstractSyntax, setExpandedAbstractSyntax] = useState<
    Record<string, boolean>
  >({});

  const [formData, setFormData] = useState({
    deviceName: '',
    description: '',
    stationName: '',
    institutionName: '',
    institutionalDepartmentName: '',

    tlsEnabled: true,
    certificateOption: 'auto-generate',

    createConnection: false,
    connectionName: '',
    hostname: HOSTNAME_OPTIONS[0],
    port: '',
    connectionDescription: '',
    connectionTlsEnabled: true,
    connectionTlsNeedClientAuth: true,

    createAE: true,
    aeConfigType: 'auto',
    aeTitle: '',
    aeName: '',
    aeDescription: '',
    aeManufacturer: '',
    aeModel: '',

    pfxFileBase64: '',
    pfxFileName: '',
    pfxPassword: '',
    certificatePem: '',
    privateKeyPem: '',
    caCertificatePem: '',
  });

  const [aeDefaultConfig, setAeDefaultConfig] =
    useState<AeDefaultConfiguration>({
      aeNamePrefix: '',
      aeTitlePrefix: '',
      defaultManufacturers: [
        {
          manufacturerName: 'Butterfly Network',
          models: [
            { modelName: 'Butterfly iQ (original)' },
            { modelName: 'Butterfly iQ+' },
            { modelName: 'Butterfly iQ+ Bladder' },
            { modelName: 'Butterfly iQ3' },
            { modelName: 'Butterfly iQ Vet' },
            { modelName: 'Butterfly iQ+ Vet' },
          ],
        },
        {
          manufacturerName: 'GE HealthCare',
          models: [
            { modelName: 'Vscan Air CL' },
            { modelName: 'Vscan Air SL' },
          ],
        },
      ],
      defaultTransferSyntaxes: [], // Will be generated from defaultSyntaxConfig
      defaultSyntaxConfig: defaultDefaultSyntaxConfig,
    });

  const [expandedDefaultAbstractSyntax, setExpandedDefaultAbstractSyntax] =
    useState<Record<string, boolean>>({});

  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      aeTitle: generateAETitle(),
      aeName: generateAEName(),
    }));
  }, []);

  useEffect(() => {
    if (formData.createAE) {
      setFormData((prev) => ({
        ...prev,
        aeName: generateAEName(),
      }));
    }
  }, [formData.deviceName, formData.createAE]);

  useEffect(() => {
    if (formData.createAE && !formData.aeTitle) {
      setFormData((prev) => ({
        ...prev,
        aeTitle: generateAETitle(),
      }));
    }
  }, [formData.createAE]);

  const handleGenerateNewAETitle = () => {
    setFormData((prev) => ({
      ...prev,
      aeTitle: generateAETitle(),
    }));
  };

  // Removed unused toggleSection function

  const toggleAbstractSyntax = (section: string) => {
    setExpandedAbstractSyntax((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const updateTransferSyntax = (
    abstractSyntax: keyof DicomSyntaxConfig,
    transferSyntax: string,
    value: TransferSyntaxValue
  ) => {
    setSyntaxConfig((prev) => ({
      ...prev,
      [abstractSyntax]: {
        ...prev[abstractSyntax],
        [transferSyntax]: value,
      },
    }));
  };

  const bulkUpdateAbstractSyntax = (
    abstractSyntax: keyof DicomSyntaxConfig,
    value: TransferSyntaxValue
  ) => {
    setSyntaxConfig((prev) => {
      const updated = { ...prev };
      Object.keys(updated[abstractSyntax]).forEach((transferSyntax) => {
        updated[abstractSyntax][transferSyntax as keyof AbstractSyntaxConfig] =
          value;
      });
      return updated;
    });
  };

  const getAcceptedCount = (abstractSyntax: keyof DicomSyntaxConfig) => {
    const config = syntaxConfig[abstractSyntax];
    return Object.values(config).filter((value) => value === 'accepted').length;
  };

  // AeDefaultConfiguration helper functions
  const addManufacturer = () => {
    setAeDefaultConfig((prev) => ({
      ...prev,
      defaultManufacturers: [
        { manufacturerName: '', models: [] }, // Add new manufacturer at the beginning
        ...prev.defaultManufacturers,
      ],
    }));
  };

  const removeManufacturer = (index: number) => {
    setAeDefaultConfig((prev) => ({
      ...prev,
      defaultManufacturers: prev.defaultManufacturers.filter(
        (_, i) => i !== index
      ),
    }));
  };

  const updateManufacturer = (index: number, manufacturerName: string) => {
    setAeDefaultConfig((prev) => ({
      ...prev,
      defaultManufacturers: prev.defaultManufacturers.map((manufacturer, i) =>
        i === index ? { ...manufacturer, manufacturerName } : manufacturer
      ),
    }));
  };

  const addModel = (manufacturerIndex: number) => {
    setAeDefaultConfig((prev) => ({
      ...prev,
      defaultManufacturers: prev.defaultManufacturers.map((manufacturer, i) =>
        i === manufacturerIndex
          ? {
              ...manufacturer,
              models: [...manufacturer.models, { modelName: '' }],
            }
          : manufacturer
      ),
    }));
  };

  const removeModel = (manufacturerIndex: number, modelIndex: number) => {
    setAeDefaultConfig((prev) => ({
      ...prev,
      defaultManufacturers: prev.defaultManufacturers.map((manufacturer, i) =>
        i === manufacturerIndex
          ? {
              ...manufacturer,
              models: manufacturer.models.filter((_, j) => j !== modelIndex),
            }
          : manufacturer
      ),
    }));
  };

  const updateModel = (
    manufacturerIndex: number,
    modelIndex: number,
    modelName: string
  ) => {
    setAeDefaultConfig((prev) => ({
      ...prev,
      defaultManufacturers: prev.defaultManufacturers.map((manufacturer, i) =>
        i === manufacturerIndex
          ? {
              ...manufacturer,
              models: manufacturer.models.map((model, j) =>
                j === modelIndex ? { modelName } : model
              ),
            }
          : manufacturer
      ),
    }));
  };

  const toggleDefaultAbstractSyntax = (section: string) => {
    setExpandedDefaultAbstractSyntax((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const updateDefaultTransferSyntaxValue = (
    abstractSyntax: keyof DefaultDicomSyntaxConfig,
    transferSyntax: string,
    value: TransferSyntaxValue
  ) => {
    setAeDefaultConfig((prev) => ({
      ...prev,
      defaultSyntaxConfig: {
        ...prev.defaultSyntaxConfig,
        [abstractSyntax]: {
          ...prev.defaultSyntaxConfig[abstractSyntax],
          [transferSyntax]: value,
        },
      },
    }));
  };

  const bulkUpdateDefaultAbstractSyntax = (
    abstractSyntax: keyof DefaultDicomSyntaxConfig,
    value: TransferSyntaxValue
  ) => {
    setAeDefaultConfig((prev) => {
      const updated = { ...prev };
      Object.keys(updated.defaultSyntaxConfig[abstractSyntax]).forEach(
        (transferSyntax) => {
          updated.defaultSyntaxConfig[abstractSyntax][
            transferSyntax as keyof DefaultAbstractSyntaxConfig
          ] = value;
        }
      );
      return updated;
    });
  };

  const getDefaultAcceptedCount = (
    abstractSyntax: keyof DefaultDicomSyntaxConfig
  ) => {
    const config = aeDefaultConfig.defaultSyntaxConfig[abstractSyntax];
    return Object.values(config).filter((value) => value === 'accepted').length;
  };

  // Transform defaultSyntaxConfig to defaultTransferSyntaxes format
  const transformDefaultSyntaxConfigToAPI = (
    syntaxConfig: DefaultDicomSyntaxConfig
  ): DefaultTransferSyntax[] => {
    const result: DefaultTransferSyntax[] = [];

    const getTransferSyntaxes = (
      config: DefaultAbstractSyntaxConfig | Partial<DefaultAbstractSyntaxConfig>
    ) => {
      const syntaxes = [];
      const mapping = {
        implicitVrLittleEndian: '1.2.840.10008.1.2',
        explicitVrLittleEndian: '1.2.840.10008.1.2.1',
        explicitVrBigEndian: '1.2.840.10008.1.2.2',
        jpegBaselineProcess1: '1.2.840.10008.1.2.4.50',
        jpegLosslessNonHierarchicalFirstOrderPredictionProcess14SelectionValue1:
          '1.2.840.10008.1.2.4.70',
        jpegLsLosslessImageCompression: '1.2.840.10008.1.2.4.80',
        jpegLsLossyNearLosslessImageCompression: '1.2.840.10008.1.2.4.81',
        jpeg2000ImageCompressionLosslessOnly: '1.2.840.10008.1.2.4.90',
        jpeg2000ImageCompression: '1.2.840.10008.1.2.4.91',
        mpeg4AvcH264HighProfile: '1.2.840.10008.1.2.4.102',
      };

      Object.entries(mapping).forEach(([key, uid]) => {
        if (config[key as keyof typeof config] === 'accepted') {
          syntaxes.push(uid);
        }
      });

      return syntaxes;
    };

    // Verification SOP Class
    const verificationTransferSyntaxes = getTransferSyntaxes(
      syntaxConfig.verificationSopClass
    );
    if (verificationTransferSyntaxes.length > 0) {
      result.push({
        abstractSyntax: '1.2.840.10008.1.1',
        abstractSyntaxName: 'Verification SOP Class',
        acceptedTransferSyntaxes: verificationTransferSyntaxes,
      });
    }

    // Secondary Capture Image Storage
    const secondaryCaptureTransferSyntaxes = getTransferSyntaxes(
      syntaxConfig.secondaryCaptureImageStorage
    );
    if (secondaryCaptureTransferSyntaxes.length > 0) {
      result.push({
        abstractSyntax: '1.2.840.10008.5.1.4.1.1.7',
        abstractSyntaxName: 'Secondary Capture Image Storage',
        acceptedTransferSyntaxes: secondaryCaptureTransferSyntaxes,
      });
    }

    // Ultrasound Image Storage
    const ultrasoundTransferSyntaxes = getTransferSyntaxes(
      syntaxConfig.ultrasoundImageStorage
    );
    if (ultrasoundTransferSyntaxes.length > 0) {
      result.push({
        abstractSyntax: '1.2.840.10008.5.1.4.1.1.6.1',
        abstractSyntaxName: 'Ultrasound Image Storage',
        acceptedTransferSyntaxes: ultrasoundTransferSyntaxes,
      });
    }

    // Ultrasound Multi-frame Image Storage
    const ultrasoundMultiFrameTransferSyntaxes = getTransferSyntaxes(
      syntaxConfig.ultrasoundMultiFrameImageStorage
    );
    if (ultrasoundMultiFrameTransferSyntaxes.length > 0) {
      result.push({
        abstractSyntax: '1.2.840.10008.5.1.4.1.1.3.1',
        abstractSyntaxName: 'Ultrasound Multi-frame Image Storage',
        acceptedTransferSyntaxes: ultrasoundMultiFrameTransferSyntaxes,
      });
    }

    return result;
  };

  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target?.result as string);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  };


  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = String(reader.result || '');
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.onerror = (e) => reject(e);
      reader.readAsDataURL(file);
    });
  };

  const handlePfxFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      const base64 = await readFileAsBase64(file);
      setFormData((prev) => ({
        ...prev,
        pfxFileBase64: base64,
        pfxFileName: file.name,
      }));
    } catch (error) {
      console.error('Error reading the PFX file:', error);
      messageI18n.error('devices.pemFileReadError');
    }
  };

  const handlePemFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldName: 'certificatePem' | 'privateKeyPem' | 'caCertificatePem'
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      const content = await readFileAsText(file);
      setFormData((prev) => ({
        ...prev,
        [fieldName]: content,
      }));
      messageI18n.success('devices.fileLoaded', { fieldName });
    } catch (error) {
      console.error(`Error reading ${fieldName} file:`, error);
      messageI18n.error('devices.fileReadFailed', { fieldName });
    }
  };

  React.useEffect(() => {
    const tlsEnabled = formData.certificateOption !== 'auto-generate-no-tls';
    setFormData((prev) => ({
      ...prev,
      port: prev.port || getFirstAvailablePort(),
      connectionName: prev.connectionName || generateConnectionName(),
      hostname: prev.hostname || HOSTNAME_OPTIONS[0],
      connectionTlsEnabled: tlsEnabled,
      connectionTlsNeedClientAuth: tlsEnabled,
    }));
  }, [devicesData, formData.certificateOption]);

  useEffect(() => {
    if (!formData.port) {
      const tlsEnabled = formData.certificateOption !== 'auto-generate-no-tls';
      setFormData((prev) => ({
        ...prev,
        port: getFirstAvailablePort(),
        connectionTlsEnabled: tlsEnabled,
        connectionTlsNeedClientAuth: tlsEnabled,
      }));
    }
  }, [devicesData, formData.certificateOption]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.deviceName.trim()) {
      messageI18n.error('devices.deviceNameRequired');
      return;
    }

    const selectedPort = formData.port || getFirstAvailablePort();
    if (!selectedPort) {
      messageI18n.error('devices.noAvailablePorts');
      return;
    }

    setIsSubmitting(true);
    try {
      const deviceData: Record<string, unknown> = {
        deviceName: formData.deviceName,
        // hostname: formData.hostname || HOSTNAME_OPTIONS[0],
        hostname: `0.0.0.0`,
        port: parseInt(selectedPort),

        ...(formData.description && { description: formData.description }),
        ...(formData.stationName && { stationName: formData.stationName }),
        ...(formData.institutionName && {
          institutionName: formData.institutionName,
        }),
        ...(formData.institutionalDepartmentName && {
          institutionalDepartmentName: formData.institutionalDepartmentName,
        }),
      };

      const tlsEnabled = formData.certificateOption !== 'auto-generate-no-tls';
      deviceData.connectionName = generateConnectionName();
      deviceData.connectionDescription = `Auto-generated connection for ${formData.deviceName}`;

      deviceData.maxOpsInvoked = 100;
      deviceData.maxOpsPerformed = 100;
      deviceData.connectTimeout = 30000;
      deviceData.requestTimeout = 30000;
      deviceData.acceptTimeout = 30000;
      deviceData.releaseTimeout = 5000;
      deviceData.responseTimeout = 60000;

      deviceData.certificateOption = formData.certificateOption;
      if (formData.certificateOption === 'upload-pfx') {
        if (!formData.pfxFileBase64) {
          message.error('Choose a PFX/PKCS12 file first');
          return;
        }
        deviceData.pfxFile = formData.pfxFileBase64;
        if (formData.pfxPassword) {
          deviceData.pfxPassword = formData.pfxPassword;
        }
        deviceData.clientAuthRequired = true;
        deviceData.connectionTlsEnabled = true;
        deviceData.connectionTlsNeedClientAuth = true;
      } else if (formData.certificateOption === 'upload-pem') {
        if (!formData.certificatePem || !formData.privateKeyPem) {
          messageI18n.error('devices.pemCertificateAndKeyRequired');
          return;
        }
        deviceData.certificatePem = formData.certificatePem;
        deviceData.privateKeyPem = formData.privateKeyPem;
        if (formData.caCertificatePem) {
          deviceData.caCertificatePem = formData.caCertificatePem;
        }
        deviceData.clientAuthRequired = true; // Always true for uploaded certificates
        deviceData.connectionTlsEnabled = true; // Always true for uploaded certificates
        deviceData.connectionTlsNeedClientAuth = true; // Always true for uploaded certificates
      } else {
        deviceData.connectionTlsEnabled = tlsEnabled;
        deviceData.connectionTlsNeedClientAuth = tlsEnabled;
      }

      const applicationEntity: Record<string, unknown> = {
        aeTitle: formData.aeTitle,
        name: formData.aeName,
        aeDescription: formData.aeDescription,
        ...(formData.aeManufacturer && {
          manufacturer: formData.aeManufacturer,
        }),
        ...(formData.aeModel && { model: formData.aeModel }),
        aeRole: 'SCP',
        acceptedCalledAeTitles: formData.aeTitle,
        installed: true,
        associator: false,
        associateAcceptor: true,
        status: 'INACTIVE',
      };

      if (formData.aeConfigType === 'custom') {
        const { transferCapabilities, services } =
          transformSyntaxConfigToAPI(syntaxConfig);

        applicationEntity.transferCapabilities = transferCapabilities;
        applicationEntity.services = services;
      } else {
        applicationEntity.transferCapabilities = [
          {
            sopClass: '1.2.840.10008.1.1',
            role: 'SCP',
            transferSyntaxes:
              '1.2.840.10008.1.2,1.2.840.10008.1.2.1,1.2.840.10008.1.2.2',
            commonName: 'Verification SOP Class',
            enabled: true,
            priority: 0,
          },
          {
            sopClass: '1.2.840.10008.5.1.4.1.1.7',
            role: 'SCP',
            transferSyntaxes:
              '1.2.840.10008.1.2,1.2.840.10008.1.2.1,1.2.840.10008.1.2.2,1.2.840.10008.1.2.4.50,1.2.840.10008.1.2.4.70,1.2.840.10008.1.2.4.80,1.2.840.10008.1.2.4.81,1.2.840.10008.1.2.4.90,1.2.840.10008.1.2.4.91,1.2.840.10008.1.2.4.102',
            commonName: 'Secondary Capture Image Storage',
            enabled: true,
            priority: 0,
          },
          {
            sopClass: '1.2.840.10008.5.1.4.1.1.6.1',
            role: 'SCP',
            transferSyntaxes:
              '1.2.840.10008.1.2,1.2.840.10008.1.2.1,1.2.840.10008.1.2.2,1.2.840.10008.1.2.4.50,1.2.840.10008.1.2.4.70,1.2.840.10008.1.2.4.80,1.2.840.10008.1.2.4.81,1.2.840.10008.1.2.4.90,1.2.840.10008.1.2.4.91,1.2.840.10008.1.2.4.102',
            commonName: 'Ultrasound Image Storage',
            enabled: true,
            priority: 0,
          },
          {
            sopClass: '1.2.840.10008.5.1.4.1.1.3.1',
            role: 'SCP',
            transferSyntaxes:
              '1.2.840.10008.1.2,1.2.840.10008.1.2.1,1.2.840.10008.1.2.2,1.2.840.10008.1.2.4.50,1.2.840.10008.1.2.4.70,1.2.840.10008.1.2.4.80,1.2.840.10008.1.2.4.81,1.2.840.10008.1.2.4.90,1.2.840.10008.1.2.4.91,1.2.840.10008.1.2.4.102',
            commonName: 'Ultrasound Multi-frame Image Storage',
            enabled: true,
            priority: 0,
          },
        ];

        applicationEntity.services = [
          {
            serviceType: 'C_ECHO',
            description: 'DICOM Verification Service with auto-generated TLS',
            enabled: true,
            priority: 1,
            sopClasses: [
              {
                sopClassUid: '1.2.840.10008.1.1',
                sopClassName: 'Verification SOP Class',
              },
            ],
          },
          {
            serviceType: 'C_STORE',
            description: 'DICOM Storage Service with auto-generated TLS',
            enabled: true,
            priority: 1,
            sopClasses: [
              {
                sopClassUid: '1.2.840.10008.5.1.4.1.1.7',
                sopClassName: 'Secondary Capture Image Storage',
              },
              {
                sopClassUid: '1.2.840.10008.5.1.4.1.1.6.1',
                sopClassName: 'Ultrasound Image Storage',
              },
              {
                sopClassUid: '1.2.840.10008.5.1.4.1.1.3.1',
                sopClassName: 'Ultrasound Multi-frame Image Storage',
              },
            ],
          },
        ];
      }

      deviceData.createAE = true;
      deviceData.applicationEntity = applicationEntity;

      // Add AeDefaultConfiguration to the device data
      const transformedDefaultTransferSyntaxes =
        transformDefaultSyntaxConfigToAPI(aeDefaultConfig.defaultSyntaxConfig);
      deviceData.aeDefaultConfiguration = {
        ...aeDefaultConfig,
        defaultTransferSyntaxes: transformedDefaultTransferSyntaxes,
        deviceId: undefined, // Will be set by the backend
      };

      delete deviceData?.aeDefaultConfiguration?.defaultSyntaxConfig;

      if (onDeviceCreated) {
        await onDeviceCreated(deviceData);

        if (refetchApplicationEntities) {
          await refetchApplicationEntities();
        }
        if (refetchDevicesWithConnections) {
          await refetchDevicesWithConnections();
        }

        messageI18n.success('devices.deviceCreated');
      }
    } catch (error) {
      console.error('Error creating device:', error);

      let errorMessage = tToast('common.genericError');

      const axiosError = error as Record<string, unknown>;
      if (axiosError?.response && typeof axiosError.response === 'object') {
        const response = axiosError.response as Record<string, unknown>;
        if (response?.data && typeof response.data === 'object') {
          const errorData = response.data as Record<string, unknown>;

          if (errorData.message && typeof errorData.message === 'string') {
            errorMessage = errorData.message;

            const errorDetails = errorData.details || errorData.error;
            if (errorDetails && typeof errorDetails === 'object') {
              const details = errorDetails as Record<string, unknown>;
              if (
                details.conflictingConnection &&
                typeof details.conflictingConnection === 'object'
              ) {
                const conflict = details.conflictingConnection as Record<
                  string,
                  unknown
                >;
                errorMessage = `Port ${conflict.port} is already in use by connection '${conflict.connectionName}' on device '${conflict.deviceName}'`;
              } else if (details.error && typeof details.error === 'string') {
                errorMessage = details.error;
              }
            }
          } else if (errorData.detail && typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          }
        }
      } else if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof (error as Record<string, unknown>).message === 'string'
      ) {
        errorMessage = (error as Record<string, unknown>).message as string;
      }

      message.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const content = (
    <div className="bg-card-color-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-title-page-color-100 dark:text-white flex items-center gap-2">
          <Monitor className="h-5 w-5 text-blue-600" />
          {t('devices.forms.createDevice.title')}
        </h2>
        <p className="text-sm text-title-page-color-300 dark:text-gray-400 mt-1">
          {t('devices.forms.createDevice.subtitle')}
        </p>
      </div>

      <div className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic {t('devices.forms.createDevice.deviceInfo')} */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2 text-title-page-color-100 dark:text-white border-b border-slate-200 dark:border-gray-700 pb-2">
              <Monitor className="h-4 w-4 text-blue-600" />
              {t('devices.forms.createDevice.deviceInfo')}
            </h3>

            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-title-page-color-300 dark:text-gray-400 mb-1">
                {t('devices.forms.createDevice.deviceName')} *
                <Tooltip content={t('devices.tooltips.deviceNameUnique')}>
                  <HelpCircle className="h-4 w-4 text-slate-500 dark:text-gray-500" />
                </Tooltip>
              </label>
              <Input
                placeholder={t('devices.forms.createDevice.deviceNamePlaceholder')}
                value={formData.deviceName}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    deviceName: (e.target as HTMLInputElement).value,
                  })
                }
                required
              />
              <p className="text-xs text-title-page-color-300 dark:text-gray-500 mt-1">
                {t('devices.forms.createDevice.deviceNameHint')}
              </p>
            </div>

            {/* Description Field */}
            {/* <div>
              <label className="flex items-center gap-2 text-sm font-medium text-title-page-color-300 dark:text-gray-400 mb-1">
                {t('devices.forms.createDevice.descriptionLabel')}
                <Tooltip content={t('devices.tooltips.deviceDescriptionOptional')}>
                  <HelpCircle className="h-4 w-4 text-slate-500 dark:text-gray-500" />
                </Tooltip>
              </label>
              <Textarea
                placeholder={t('devices.forms.createDevice.deviceDescriptionPlaceholderLong')}
                className="min-h-[80px]"
                value={formData.description}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    description: (e.target as HTMLTextAreaElement).value,
                  })
                }
              />
            </div> */}
          </div>

          {/* {t('devices.forms.createDevice.securityConfig')} Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold flex items-center gap-2 text-title-page-color-100 dark:text-white border-b border-slate-200 dark:border-gray-700 pb-2">
              <Shield className="h-4 w-4 text-blue-600" />
              {t('devices.forms.createDevice.securityConfig')}
            </h3>

            {/* TLS {t('devices.forms.createDevice.certificateConfig')} */}
            {formData.tlsEnabled && (
              <div className="bg-card-color-100 dark:bg-gray-800 border border-slate-300 dark:border-gray-600 rounded-lg">
                <div className="p-4 border-b border-slate-200 dark:border-gray-700">
                  <h4 className="text-base font-medium flex items-center gap-2 text-title-page-color-100 dark:text-white">
                    <Shield className="h-4 w-4 text-blue-600" />
                    {t('devices.forms.createDevice.certificateConfig')}
                  </h4>
                  <p className="text-sm text-slate-600 dark:text-gray-400 mt-1">
                    {t('devices.forms.createDevice.certificateConfigHint')}
                  </p>
                </div>
                <div className="p-4 space-y-4">
                  <div className="grid grid-cols-1 gap-4">

                    <div
                      className={`flex items-center space-x-2 rounded-lg border p-4 cursor-pointer transition-colors ${
                        formData.certificateOption === 'upload-pfx'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-slate-200 dark:border-gray-600 hover:bg-card-color-100 dark:hover:bg-gray-700'
                      }`}
                      onClick={() =>
                        setFormData({
                          ...formData,
                          certificateOption: 'upload-pfx',
                        })
                      }
                    >
                      <input
                        type="radio"
                        value="upload-pfx"
                        checked={formData.certificateOption === 'upload-pfx'}
                        onChange={() =>
                          setFormData({
                            ...formData,
                            certificateOption: 'upload-pfx',
                          })
                        }
                        className="text-blue-600"
                      />
                      <div className="flex items-center gap-2">
                        <Upload className="h-4 w-4 text-blue-600" />
                        <span className="text-title-page-color-100 dark:text-white font-medium">
                          Upload PFX / PKCS12
                        </span>
                        <span className="text-xs text-slate-500 dark:text-gray-400">
                          certificate, key and chain in one file
                        </span>
                      </div>
                    </div>
                    <div
                      className={`flex items-center space-x-2 rounded-lg border p-4 cursor-pointer transition-colors ${
                        formData.certificateOption === 'upload-pem'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-slate-200 dark:border-gray-600 hover:bg-card-color-100 dark:hover:bg-gray-700'
                      }`}
                      onClick={() =>
                        setFormData({
                          ...formData,
                          certificateOption: 'upload-pem',
                        })
                      }
                    >
                      <input
                        type="radio"
                        value="upload-pem"
                        checked={formData.certificateOption === 'upload-pem'}
                        onChange={() =>
                          setFormData({
                            ...formData,
                            certificateOption: 'upload-pem',
                          })
                        }
                        className="text-blue-600"
                      />
                      <div className="flex items-center gap-2">
                        <Upload className="h-4 w-4 text-blue-600" />
                        <span className="text-title-page-color-100 dark:text-white font-medium">
                          {t('devices.forms.createDevice.uploadPem')}
                        </span>
                      </div>
                    </div>
                    <div
                      className={`flex items-center space-x-2 rounded-lg border p-4 cursor-pointer transition-colors ${
                        formData.certificateOption === 'auto-generate'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-slate-200 dark:border-gray-600 hover:bg-card-color-100 dark:hover:bg-gray-700'
                      }`}
                      onClick={() =>
                        setFormData({
                          ...formData,
                          certificateOption: 'auto-generate',
                        })
                      }
                    >
                      <input
                        type="radio"
                        value="auto-generate"
                        checked={formData.certificateOption === 'auto-generate'}
                        onChange={() =>
                          setFormData({
                            ...formData,
                            certificateOption: 'auto-generate',
                          })
                        }
                        className="text-blue-600"
                      />
                      <div className="flex items-center gap-2">
                        <Zap className="h-4 w-4 text-blue-600" />
                        <span className="text-blue-800 dark:text-blue-400 font-medium">
                          {t('devices.forms.createDevice.autoGenerateWithTls')}
                        </span>
                      </div>
                    </div>
                    <div
                      className={`flex items-center space-x-2 rounded-lg border p-4 cursor-pointer transition-colors ${
                        formData.certificateOption === 'auto-generate-no-tls'
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-slate-200 dark:border-gray-600 hover:bg-card-color-100 dark:hover:bg-gray-700'
                      }`}
                      onClick={() =>
                        setFormData({
                          ...formData,
                          certificateOption: 'auto-generate-no-tls',
                        })
                      }
                    >
                      <input
                        type="radio"
                        value="auto-generate-no-tls"
                        checked={
                          formData.certificateOption === 'auto-generate-no-tls'
                        }
                        onChange={() =>
                          setFormData({
                            ...formData,
                            certificateOption: 'auto-generate-no-tls',
                          })
                        }
                        className="text-blue-600"
                      />
                      <div className="flex items-center gap-2">
                        <Monitor className="h-4 w-4 text-blue-600" />
                        <span className="text-gray-800 dark:text-gray-400 font-medium">
                          {t('devices.forms.createDevice.autoGenerateWithoutTls')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {formData.certificateOption === 'upload-pfx' && (
                    <div className="space-y-4 mt-4">
                      <div className="rounded-lg border border-slate-300 bg-card-color-100 p-3 dark:border-gray-600 dark:bg-gray-800">
                        <div className="flex items-start gap-2 text-slate-600 dark:text-gray-400">
                          <HelpCircle className="mt-0.5 h-4 w-4 shrink-0" />
                          <span className="text-sm">
                            The bundle is unpacked on the server into its
                            certificate, private key and chain. A PFX holding only
                            the certificate and no private key cannot be used —
                            export it again with the key included.
                          </span>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm font-medium text-title-page-color-300 dark:text-gray-400">
                          <FileText className="h-4 w-4 text-blue-600" />
                          PFX / PKCS12 file
                          <span className="text-red-500">*</span>
                        </label>
                        <Input
                          type="file"
                          accept=".pfx,.p12"
                          onChange={handlePfxFileUpload}
                          className={formData.pfxFileBase64 ? 'border-green-500' : ''}
                        />
                        {formData.pfxFileBase64 && (
                          <p className="text-xs text-green-600 dark:text-green-400">
                            ✓ {formData.pfxFileName} loaded
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <label className="flex items-center gap-2 text-sm font-medium text-title-page-color-300 dark:text-gray-400">
                          PFX password
                          <span className="text-xs font-normal text-slate-500">
                            leave blank if the bundle has none
                          </span>
                        </label>
                        <Input
                          type="password"
                          autoComplete="new-password"
                          placeholder="Enter password if encrypted"
                          value={formData.pfxPassword}
                          onChange={(e) =>
                            setFormData({ ...formData, pfxPassword: e.target.value })
                          }
                        />
                      </div>
                    </div>
                  )}

                  {formData.certificateOption === 'upload-pem' && (
                    <div className="space-y-4 mt-4">
                      <div className="bg-card-color-100 dark:bg-gray-800 border border-slate-300 dark:border-gray-600 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-slate-600 dark:text-gray-400">
                          <HelpCircle className="h-4 w-4" />
                          <span className="text-sm">
                            {t('devices.common.uploadAllPemRequired')}
                          </span>
                        </div>
                      </div>

                      <div className="grid gap-4">
                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-sm font-medium text-title-page-color-300 dark:text-gray-400">
                            <FileText className="h-4 w-4 text-blue-600" />
                            {t('devices.forms.createDevice.certificatePem')}
                            <Tooltip content={t('devices.tooltips.serverCertPem')}>
                              <HelpCircle className="h-3 w-3 text-slate-500 dark:text-gray-500" />
                            </Tooltip>
                          </label>
                          <Input
                            type="file"
                            accept=".crt,.pem"
                            onChange={(e) =>
                              handlePemFileUpload(e, 'certificatePem')
                            }
                            className={
                              formData.certificatePem ? 'border-green-500' : ''
                            }
                          />
                          {formData.certificatePem && (
                            <p className="text-xs text-green-600 dark:text-green-400">
                              ✓ {t('devices.common.certificateFileLoaded')}
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-sm font-medium text-title-page-color-300 dark:text-gray-400">
                            <Key className="h-4 w-4 text-blue-600" />
                            {t('devices.forms.createDevice.privateKeyPem')}
                            <Tooltip content={t('devices.tooltips.privateKeyPem')}>
                              <HelpCircle className="h-3 w-3 text-slate-500 dark:text-gray-500" />
                            </Tooltip>
                          </label>
                          <Input
                            type="file"
                            accept=".key,.pem"
                            onChange={(e) =>
                              handlePemFileUpload(e, 'privateKeyPem')
                            }
                            className={
                              formData.privateKeyPem ? 'border-green-500' : ''
                            }
                          />
                          {formData.privateKeyPem && (
                            <p className="text-xs text-green-600 dark:text-green-400">
                              ✓ {t('devices.common.privateKeyFileLoaded')}
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <label className="flex items-center gap-2 text-sm font-medium text-title-page-color-300 dark:text-gray-400">
                            <Shield className="h-4 w-4 text-blue-600" />
                            {t('devices.forms.createDevice.caCertificatePem')}
                            <Tooltip content={t('devices.tooltips.caCertPem')}>
                              <HelpCircle className="h-3 w-3 text-slate-500 dark:text-gray-500" />
                            </Tooltip>
                          </label>
                          <Input
                            type="file"
                            accept=".crt,.pem"
                            onChange={(e) =>
                              handlePemFileUpload(e, 'caCertificatePem')
                            }
                            className={
                              formData.caCertificatePem
                                ? 'border-green-500'
                                : ''
                            }
                          />
                          {formData.caCertificatePem && (
                            <p className="text-xs text-green-600 dark:text-green-400">
                              ✓ {t('devices.forms.createDevice.caCertFileLoaded')}
                            </p>
                          )}
                        </div>

                        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3">
                          <div className="flex items-center gap-2 text-amber-800 dark:text-amber-400">
                            <Info className="h-4 w-4" />
                            <span className="text-sm font-medium">
                              {t('devices.forms.createDevice.pemRequirementsTitle')}
                            </span>
                          </div>
                          <ul className="text-sm text-amber-700 dark:text-amber-300 mt-1 ml-6 list-disc">
                            <li>
                              {t('devices.forms.createDevice.certAndKeyRequired')}
                            </li>
                            <li>{t('devices.common.pemFormatHint')}</li>
                            <li>{t('devices.common.caCertOptionalHint')}</li>
                            <li>
                              {t('devices.forms.createDevice.filesValidHint')}
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.certificateOption === 'auto-generate' && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-blue-800 dark:text-blue-400">
                        <Zap className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          {t('devices.forms.createDevice.autoGenerateWithTls')}
                        </span>
                      </div>
                      <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                        {t('devices.forms.createDevice.autoGenerateWithTlsHint')}
                      </p>
                    </div>
                  )}

                  {formData.certificateOption === 'auto-generate-no-tls' && (
                    <div className="bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-gray-800 dark:text-gray-400">
                        <Monitor className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          {t('devices.forms.createDevice.autoGenerateWithoutTls')}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                        {t('devices.forms.createDevice.autoGenerateWithoutTlsHint')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* AE Configuration Button */}
          <div className="border-t border-slate-200 dark:border-gray-700 pt-6">
            <Button
              type="button"
              onClick={() => setShowAeDefaultConfig(!showAeDefaultConfig)}
              variant="outline"
              className="w-full flex items-center justify-between"
            >
              <span className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                {t('devices.forms.createDevice.advancedConfig')}
              </span>
              {showAdvanced ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
            <p className="text-xs text-title-page-color-300 dark:text-gray-500 mt-2">
              {t('devices.forms.createDevice.configureAeDefault')}
            </p>
          </div>

          {/* {t('devices.forms.createDevice.aeDefaultConfig')} Section */}
          {showAeDefaultConfig && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold flex items-center gap-2 text-title-page-color-100 dark:text-white border-b border-slate-200 dark:border-gray-700 pb-2">
                <Settings className="h-4 w-4 text-blue-600" />
                {t('devices.forms.createDevice.aeDefaultConfig')}
              </h3>

              <div className="bg-card-color-100 dark:bg-gray-800 border border-slate-300 dark:border-gray-600 rounded-lg p-4 space-y-4">
                {/* {t('devices.forms.createDevice.prefixConfig')} */}
                <div className="space-y-4">
                  <h4 className="text-base font-medium text-title-page-color-100 dark:text-white">
                    {t('devices.forms.createDevice.prefixConfig')}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-title-page-color-300 dark:text-gray-400 mb-1">
                        {t('devices.forms.createDevice.aeNamePrefix')}
                        <Tooltip content={t('devices.tooltips.aeNamePrefix')}>
                          <HelpCircle className="h-4 w-4 text-slate-500 dark:text-gray-500" />
                        </Tooltip>
                      </label>
                      <Input
                        placeholder="DEEPECHO_"
                        value={aeDefaultConfig.aeNamePrefix}
                        onChange={(e) => {
                          const value = (
                            e.target as HTMLInputElement
                          ).value.slice(0, 15);
                          setAeDefaultConfig((prev) => ({
                            ...prev,
                            aeNamePrefix: value,
                          }));
                        }}
                        maxLength={15}
                      />
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-title-page-color-300 dark:text-gray-400 mb-1">
                        {t('devices.forms.createDevice.aeTitlePrefix')}
                        <Tooltip content={t('devices.tooltips.aeTitlePrefix')}>
                          <HelpCircle className="h-4 w-4 text-slate-500 dark:text-gray-500" />
                        </Tooltip>
                      </label>
                      <Input
                        placeholder="DE_"
                        value={aeDefaultConfig.aeTitlePrefix}
                        onChange={(e) => {
                          const value = (
                            e.target as HTMLInputElement
                          ).value.slice(0, 4);
                          setAeDefaultConfig((prev) => ({
                            ...prev,
                            aeTitlePrefix: value,
                          }));
                        }}
                        maxLength={4}
                      />
                    </div>
                  </div>
                </div>

                {/* {t('devices.forms.createDevice.defaultManufacturers')} */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="text-base font-semibold text-title-page-color-100 dark:text-white">
                        {t('devices.forms.createDevice.defaultManufacturers')}
                      </h4>
                      <p className="text-sm text-title-page-color-300 dark:text-gray-400 mt-1">
{t('devices.forms.createDevice.defaultManufacturersHint')}
                      </p>
                    </div>
                    <Button
                      type="button"
                      onClick={addManufacturer}
                      variant="default"
                      size="sm"
                      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                    >
                      <span className="text-lg leading-none">+</span>
                      {t('devices.forms.createDevice.addManufacturer')}
                    </Button>
                  </div>

                  <div className="space-y-4">
                    {aeDefaultConfig.defaultManufacturers.map(
                      (manufacturer, manufacturerIndex) => (
                        <div
                          key={manufacturerIndex}
                          className="bg-gradient-to-r from-slate-50 to-gray-50 dark:from-gray-800 dark:to-gray-750 border border-slate-200 dark:border-gray-600 rounded-xl p-6 space-y-5 shadow-sm hover:shadow-md transition-shadow duration-200"
                        >
                          {/* manufacturer header */}
                          <div className="flex items-start gap-4">
                            <div className="flex-1 space-y-2">
                              <label className="block text-sm font-medium text-title-page-color-100 dark:text-white">
                                {t('devices.forms.createDevice.manufacturer')}
                              </label>
                              <CreatableSelect
                                value={manufacturer.manufacturerName}
                                options={MANUFACTURER_OPTIONS}
                                onChange={(value) => {
                                  updateManufacturer(manufacturerIndex, value);
                                }}
                                placeholder={t('devices.forms.createDevice.manufacturerPlaceholderLong')}
                                className="w-full"
                              />
                            </div>
                            <div className="flex items-end h-full pt-6">
                              <Button
                                type="button"
                                onClick={() =>
                                  removeManufacturer(manufacturerIndex)
                                }
                                variant="outline"
                                size="sm"
                                className="text-red-600 border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300 transition-colors duration-200"
                              >
                                <span className="sr-only">
                                  {t('devices.forms.createDevice.removeManufacturer')}
                                </span>
                                <svg
                                  className="h-4 w-4"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </Button>
                            </div>
                          </div>

                          {/* Models Section */}
                          <div className="space-y-4">
                            <div>
                              <label className="text-sm font-medium text-title-page-color-100 dark:text-white">
                                {t('devices.forms.createDevice.supportedModels')}
                              </label>
                              <p className="text-xs text-title-page-color-300 dark:text-gray-400 mt-0.5">
{t('devices.forms.createDevice.supportedModelsHint')}
                              </p>
                            </div>

                            <div className="space-y-3">
                              {/* Model Selection/Creation Input */}
                              <CreatableSelect
                                value=""
                                options={
                                  manufacturer.manufacturerName &&
                                  MANUFACTURER_MODEL_DATA[
                                    manufacturer.manufacturerName
                                  ]
                                    ? MANUFACTURER_MODEL_DATA[
                                        manufacturer.manufacturerName
                                      ].filter(
                                        (option) =>
                                          !manufacturer.models.some(
                                            (model) =>
                                              model.modelName === option.value
                                          )
                                      )
                                    : []
                                }
                                onChange={(value) => {
                                  if (
                                    value &&
                                    !manufacturer.models.some(
                                      (model) => model.modelName === value
                                    )
                                  ) {
                                    addModel(manufacturerIndex);
                                    updateModel(
                                      manufacturerIndex,
                                      manufacturer.models.length,
                                      value
                                    );
                                  }
                                }}
                                placeholder={
                                  manufacturer.manufacturerName
                                    ? t('devices.forms.createDevice.selectOrCreateModelFor', { manufacturer: manufacturer.manufacturerName })
                                    : t('devices.common.selectManufacturerFirst')
                                }
                                isDisabled={!manufacturer.manufacturerName}
                                formatCreateLabel={(inputValue) =>
                                  t('devices.forms.createDevice.createModelLabel', {
                                    value: inputValue,
                                  })
                                }
                                createOptionPosition="first"
                                noOptionsMessage={() =>
                                  t('devices.common.typeToCreateModel')
                                }
                              />

                              {/* Selected Models List */}
                              {manufacturer.models.length > 0 && (
                                <div className="bg-white dark:bg-gray-800 border border-slate-200 dark:border-gray-600 rounded-lg divide-y divide-slate-200 dark:divide-gray-600">
                                  <div className="px-4 py-2 bg-slate-50 dark:bg-gray-700 rounded-t-lg">
                                    <div className="flex items-center justify-between">
                                      <span className="text-sm font-medium text-title-page-color-100 dark:text-white">
                                        {t('devices.forms.createDevice.selectedModels', { count: manufacturer.models.length })}
                                      </span>
                                      <span className="text-xs text-title-page-color-300 dark:text-gray-400">
                                        {t('devices.forms.createDevice.clickToRemove')}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="max-h-48 overflow-y-auto">
                                    {manufacturer.models.map(
                                      (model, modelIndex) => (
                                        <div
                                          key={modelIndex}
                                          className="flex items-center justify-between px-4 py-3 hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors duration-200"
                                        >
                                          <div className="flex items-center gap-3">
                                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                            <span className="text-sm text-title-page-color-100 dark:text-white font-medium">
                                              {model.modelName}
                                            </span>
                                          </div>
                                          <Button
                                            type="button"
                                            onClick={() =>
                                              removeModel(
                                                manufacturerIndex,
                                                modelIndex
                                              )
                                            }
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 p-1.5 transition-colors duration-200"
                                          >
                                            <span className="sr-only">
                                              {t('devices.forms.createDevice.removeModel', { name: model.modelName })}
                                            </span>
                                            <svg
                                              className="h-4 w-4"
                                              fill="none"
                                              viewBox="0 0 24 24"
                                              stroke="currentColor"
                                            >
                                              <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M6 18L18 6M6 6l12 12"
                                              />
                                            </svg>
                                          </Button>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}

                              {manufacturer.models.length === 0 && (
                                <div className="text-center py-6 px-4 border-2 border-dashed border-slate-200 dark:border-gray-600 rounded-lg bg-slate-50 dark:bg-gray-800/50">
                                  <svg
                                    className="mx-auto h-6 w-6 text-slate-400 dark:text-gray-500 mb-2"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={1.5}
                                      d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
                                    />
                                  </svg>
                                  <p className="text-sm text-slate-500 dark:text-gray-400">
                                    {t('devices.forms.createDevice.noModelsSelected')}
                                  </p>
                                  <p className="text-xs text-slate-400 dark:text-gray-500 mt-1">
                                    {t('devices.forms.createDevice.noModelsSelectedHint')}
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )
                    )}
                  </div>

                  {aeDefaultConfig.defaultManufacturers.length === 0 && (
                    <div className="text-center py-12 px-6 border-2 border-dashed border-slate-200 dark:border-gray-600 rounded-xl bg-slate-50 dark:bg-gray-800/50">
                      <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
                        <svg
                          className="h-8 w-8 text-blue-600 dark:text-blue-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1.5}
                            d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"
                          />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-title-page-color-100 dark:text-white mb-2">
                        {t('devices.forms.createDevice.noManufacturersAdded')}
                      </h3>
                      <p className="text-sm text-slate-500 dark:text-gray-400 mb-4 max-w-sm mx-auto">
                        {t('devices.forms.createDevice.emptyManufacturersHint')}
                      </p>
                      <Button
                        type="button"
                        onClick={addManufacturer}
                        variant="default"
                        className="bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                      >
                        <span className="text-lg leading-none mr-2">+</span>
                        {t('devices.forms.createDevice.addFirstManufacturer')}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Default Abstract Syntaxes & Transfer Syntaxes */}
                <div className="space-y-4">
                  <h4 className="text-base font-medium text-title-page-color-100 dark:text-white border-b border-slate-200 dark:border-gray-700 pb-2">
                    {t('devices.forms.createDevice.defaultAbstractSyntaxes')}
                  </h4>

                  <div className="space-y-4">
                    {Object.entries(abstractSyntaxInfo).map(([key, info]) => {
                      const abstractKey = key as keyof DefaultDicomSyntaxConfig;
                      const config =
                        aeDefaultConfig.defaultSyntaxConfig[abstractKey];
                      const acceptedCount =
                        getDefaultAcceptedCount(abstractKey);
                      const totalCount = Object.keys(config).length;

                      return (
                        <div
                          key={key}
                          className="border border-slate-200 dark:border-gray-700 rounded-lg"
                        >
                          <div
                            className="p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
                            onClick={() => toggleDefaultAbstractSyntax(key)}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                {expandedDefaultAbstractSyntax[key] ? (
                                  <ChevronDown className="h-4 w-4 text-slate-500 dark:text-gray-400" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-slate-500 dark:text-gray-400" />
                                )}
                                <div>
                                  <h4 className="text-base font-medium text-title-page-color-100 dark:text-white">
                                    {tDicomAbstractName(key)}
                                  </h4>
                                  <p className="text-sm text-title-page-color-300 dark:text-gray-400">
                                    {tDicomAbstractDescription(key)}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge
                                  className={
                                    acceptedCount > 0
                                      ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                                      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                  }
                                >
                                  {t('devices.forms.createDevice.acceptedCountBadge', { accepted: acceptedCount, total: totalCount })}
                                </Badge>
                              </div>
                            </div>
                          </div>

                          {expandedDefaultAbstractSyntax[key] && (
                            <div className="px-4 pb-4">
                              <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                  <div className="text-sm text-title-page-color-300 dark:text-gray-400">
                                    <strong>{t('devices.common.sopClassUid')}</strong>{' '}
                                    {info.sopClassUid}
                                  </div>
                                  <div className="flex gap-2">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        bulkUpdateDefaultAbstractSyntax(
                                          abstractKey,
                                          'accepted'
                                        )
                                      }
                                      className="text-green-600 border-green-200 hover:bg-green-50 dark:hover:bg-green-900/20"
                                    >
                                      {t('devices.common.acceptAll')}
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() =>
                                        bulkUpdateDefaultAbstractSyntax(
                                          abstractKey,
                                          'rejected'
                                        )
                                      }
                                      className="text-red-600 border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20"
                                    >
                                      {t('devices.common.rejectAll')}
                                    </Button>
                                  </div>
                                </div>

                                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                                  <div className="flex items-center gap-2 text-blue-800 dark:text-blue-400">
                                    <AlertTriangle className="h-4 w-4" />
                                    <span className="text-sm font-medium">
                                      {tDicomAbstractSecurityNote(key)}
                                    </span>
                                  </div>
                                </div>

                                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                  {/* Transfer Syntax Controls */}
                                  <div className="space-y-3">
                                    <h4 className="font-medium text-title-page-color-100 dark:text-white">
                                      {t('devices.common.transferSyntaxSupport')}
                                    </h4>

                                    {/* Uncompressed Transfer Syntaxes */}
                                    <div className="space-y-2">
                                      <h5 className="text-sm font-medium text-title-page-color-300 dark:text-gray-400">
                                        {t('devices.common.uncompressed')}
                                      </h5>
                                      <div className="grid grid-cols-1 gap-2">
                                        {Object.entries(transferSyntaxInfo)
                                          .filter(
                                            ([_, info]) =>
                                              info.type === 'Uncompressed'
                                          )
                                          .map(
                                            ([transferKey, transferInfo]) => {
                                              if (!(transferKey in config)) {
                                                return null;
                                              }
                                              const value =
                                                config[
                                                  transferKey as keyof DefaultAbstractSyntaxConfig
                                                ];
                                              return (
                                                <div
                                                  key={transferKey}
                                                  className="flex items-center justify-between p-2 rounded border border-slate-200 dark:border-gray-600 bg-card-color-100 dark:bg-gray-800"
                                                >
                                                  <div className="flex-1">
                                                    <div className="text-sm font-medium text-title-page-color-100 dark:text-white">
                                                      {tDicomTransferSyntaxName(transferKey)}
                                                    </div>
                                                    <div className="text-xs text-title-page-color-300 dark:text-gray-400 font-mono">
                                                      {transferInfo.uid}
                                                    </div>
                                                  </div>
                                                  <Select
                                                    value={value}
                                                    onChange={(e) =>
                                                      updateDefaultTransferSyntaxValue(
                                                        abstractKey,
                                                        transferKey,
                                                        e.target
                                                          .value as TransferSyntaxValue
                                                      )
                                                    }
                                                    className="w-24"
                                                  >
                                                    <option
                                                      value="accepted"
                                                      className="text-green-600"
                                                    >
                                                      {t('devices.forms.createDevice.accept')}
                                                    </option>
                                                    <option
                                                      value="rejected"
                                                      className="text-red-600"
                                                    >
                                                      {t('devices.forms.createDevice.reject')}
                                                    </option>
                                                  </Select>
                                                </div>
                                              );
                                            }
                                          )}
                                      </div>
                                    </div>

                                    {/* Compressed Transfer Syntaxes */}
                                    <div className="space-y-2">
                                      <h5 className="text-sm font-medium text-title-page-color-300 dark:text-gray-400">
                                        {t('devices.common.compressed')}
                                      </h5>
                                      <div className="grid grid-cols-1 gap-2">
                                        {Object.entries(transferSyntaxInfo)
                                          .filter(
                                            ([_, info]) =>
                                              info.type === 'Compressed'
                                          )
                                          .map(
                                            ([transferKey, transferInfo]) => {
                                              if (!(transferKey in config)) {
                                                return null;
                                              }
                                              const value =
                                                config[
                                                  transferKey as keyof DefaultAbstractSyntaxConfig
                                                ];
                                              return (
                                                <div
                                                  key={transferKey}
                                                  className="flex items-center justify-between p-2 rounded border border-slate-200 dark:border-gray-600 bg-card-color-100 dark:bg-gray-800"
                                                >
                                                  <div className="flex-1">
                                                    <div className="text-sm font-medium text-title-page-color-100 dark:text-white">
                                                      {tDicomTransferSyntaxName(transferKey)}
                                                    </div>
                                                    <div className="text-xs text-title-page-color-300 dark:text-gray-400 font-mono">
                                                      {transferInfo.uid}
                                                    </div>
                                                  </div>
                                                  <Select
                                                    value={value}
                                                    onChange={(e) =>
                                                      updateDefaultTransferSyntaxValue(
                                                        abstractKey,
                                                        transferKey,
                                                        e.target
                                                          .value as TransferSyntaxValue
                                                      )
                                                    }
                                                    className="w-24"
                                                  >
                                                    <option
                                                      value="accepted"
                                                      className="text-green-600"
                                                    >
                                                      {t('devices.forms.createDevice.accept')}
                                                    </option>
                                                    <option
                                                      value="rejected"
                                                      className="text-red-600"
                                                    >
                                                      {t('devices.forms.createDevice.reject')}
                                                    </option>
                                                  </Select>
                                                </div>
                                              );
                                            }
                                          )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* {t('devices.forms.createDevice.advancedConfig')} Button */}
          {/* <div className="border-t border-slate-200 dark:border-gray-700 pt-6">
            <Button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              variant="outline"
              className="w-full flex items-center justify-between"
            >
              <span className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                {t('devices.forms.createDevice.advancedConfig')}
              </span>
              {showAdvanced ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
            <p className="text-xs text-title-page-color-300 dark:text-gray-500 mt-2">
              Configure additional device details, connections, and application
              entities
            </p>
          </div> */}

          {/* {t('devices.forms.createDevice.advancedConfig')} Sections */}
          {showAdvanced && (
            <div className="space-y-6 border border-slate-200 dark:border-gray-600 rounded-lg p-6 bg-slate-50 dark:bg-gray-700">
              {/* Extended {t('devices.forms.createDevice.deviceInfo')} */}
              {/* <div className="space-y-4">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => toggleSection('deviceInfo')}
                >
                  <h4 className="text-base font-semibold flex items-center gap-2 text-title-page-color-100 dark:text-white">
                    {expandedSections.deviceInfo ? (
                      <ChevronDown className="h-4 w-4 text-slate-500" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-slate-500" />
                    )}
                    <Monitor className="h-4 w-4 text-blue-600" />
                    Extended {t('devices.forms.createDevice.deviceInfo')}
                  </h4>
                </div>

                {expandedSections.deviceInfo && (
                  <div className="space-y-4 pl-6">
                    <div>
                      <label className="block text-sm font-medium text-title-page-color-300 dark:text-gray-400 mb-1">
                        {t('devices.forms.createDevice.stationNameLabel')}
                      </label>
                      <Input
                        placeholder={t('devices.forms.createDevice.stationNamePlaceholder')}
                        value={formData.stationName}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            stationName: (e.target as HTMLInputElement).value,
                          })
                        }
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-title-page-color-300 dark:text-gray-400 mb-1">
                          {t('devices.forms.createDevice.institutionNameLabel')}
                        </label>
                        <Input
                          placeholder={t('devices.forms.createDevice.institutionPlaceholder')}
                          value={formData.institutionName}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              institutionName: (e.target as HTMLInputElement)
                                .value,
                            })
                          }
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-title-page-color-300 dark:text-gray-400 mb-1">
                          {t('devices.forms.createDevice.departmentNameLabel')}
                        </label>
                        <Input
                          placeholder={t('devices.forms.createDevice.departmentPlaceholder')}
                          value={formData.institutionalDepartmentName}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              institutionalDepartmentName: (
                                e.target as HTMLInputElement
                              ).value,
                            })
                          }
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div> */}

              {/* {t('devices.forms.createDevice.aeConfigSectionTitle')} */}
              <div className="space-y-4 border-t border-slate-300 dark:border-gray-600 pt-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-base font-semibold flex items-center gap-2 text-title-page-color-100 dark:text-white">
                    <Server className="h-4 w-4 text-blue-600" />
                    {t('devices.forms.createDevice.aeConfigSectionTitle')}
                  </h4>
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-full px-3 py-1">
                    <span className="text-xs font-medium text-green-800 dark:text-green-400">
                      {t('devices.forms.createDevice.alwaysCreated')}
                    </span>
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-blue-800 dark:text-blue-400">
                    <Info className="h-4 w-4" />
                    <span className="text-sm">
                      {t('devices.forms.createDevice.aeAlwaysCreatedHint')}
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="bg-card-color-100 dark:bg-gray-800 border border-slate-300 dark:border-gray-600 rounded-lg p-4">
                    <h5 className="text-sm font-medium text-title-page-color-100 dark:text-white mb-3">
                      {t('devices.forms.createDevice.configType')}
                    </h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div
                        className={`flex items-center space-x-2 rounded-lg border p-3 cursor-pointer transition-colors ${
                          formData.aeConfigType === 'auto'
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-slate-200 dark:border-gray-600 hover:bg-slate-50 dark:hover:bg-gray-700'
                        }`}
                        onClick={() =>
                          setFormData({ ...formData, aeConfigType: 'auto' })
                        }
                      >
                        <input
                          type="radio"
                          value="auto"
                          checked={formData.aeConfigType === 'auto'}
                          onChange={() =>
                            setFormData({ ...formData, aeConfigType: 'auto' })
                          }
                          className="text-blue-600"
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <Zap className="h-4 w-4 text-blue-600" />
                            <span className="text-title-page-color-100 dark:text-white font-medium">
                              {t('devices.forms.createDevice.autoConfiguration')}
                            </span>
                          </div>
                          <p className="text-xs text-title-page-color-300 dark:text-gray-400 mt-1">
                            {t('devices.forms.createDevice.autoConfigurationHint')}
                          </p>
                        </div>
                      </div>
                      <div
                        className={`flex items-center space-x-2 rounded-lg border p-3 cursor-pointer transition-colors ${
                          formData.aeConfigType === 'custom'
                            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                            : 'border-slate-200 dark:border-gray-600 hover:bg-slate-50 dark:hover:bg-gray-700'
                        }`}
                        onClick={() =>
                          setFormData({ ...formData, aeConfigType: 'custom' })
                        }
                      >
                        <input
                          type="radio"
                          value="custom"
                          checked={formData.aeConfigType === 'custom'}
                          onChange={() =>
                            setFormData({
                              ...formData,
                              aeConfigType: 'custom',
                            })
                          }
                          className="text-blue-600"
                        />
                        <div>
                          <div className="flex items-center gap-2">
                            <Settings className="h-4 w-4 text-blue-600" />
                            <span className="text-title-page-color-100 dark:text-white font-medium">
                              {t('devices.forms.createDevice.customConfiguration')}
                            </span>
                          </div>
                          <p className="text-xs text-title-page-color-300 dark:text-gray-400 mt-1">
                            {t('devices.forms.createDevice.customConfigurationHint')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* AE Basic Settings */}
                  <div className="space-y-4 pl-6">
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-title-page-color-300 dark:text-gray-400 mb-1">
                        {t('devices.forms.createDevice.nameLabel')}
                        <Tooltip content={t('devices.tooltips.aeNameOptional')}>
                          <HelpCircle className="h-4 w-4 text-slate-500 dark:text-gray-500" />
                        </Tooltip>
                      </label>
                      <Input
                        placeholder={t('devices.forms.createDevice.aeNamePlaceholder')}
                        value={formData.aeName}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            aeName: (e.target as HTMLInputElement).value,
                          })
                        }
                      />
                      <p className="text-xs text-title-page-color-300 dark:text-gray-500 mt-1">
                        {t('devices.forms.createDevice.aeFriendlyNameHint')}
                      </p>
                    </div>

                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-title-page-color-300 dark:text-gray-400 mb-1">
                        {t('devices.forms.createDevice.aeTitleLabel')} *
                        <Tooltip content={t('devices.tooltips.aeTitleDicom')}>
                          <HelpCircle className="h-4 w-4 text-slate-500 dark:text-gray-500" />
                        </Tooltip>
                      </label>
                      <div className="flex gap-2">
                        <Input
                          placeholder={t('devices.forms.createAe.aeTitlePlaceholder')}
                          value={formData.aeTitle}
                          onChange={(e) => {
                            const value = (e.target as HTMLInputElement).value
                              .toUpperCase()
                              .replace(/[^A-Z0-9_]/g, '')
                              .slice(0, 15);
                            setFormData({ ...formData, aeTitle: value });
                          }}
                          required={formData.createAE}
                          className="flex-1"
                          maxLength={15}
                        />
                        <Button
                          type="button"
                          onClick={handleGenerateNewAETitle}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-2 bg-transparent"
                        >
                          <RefreshCw className="h-4 w-4" />
                          {t('devices.forms.createDevice.generate')}
                        </Button>
                      </div>
                      <p className="text-xs text-title-page-color-300 dark:text-gray-500 mt-1">
                        {t('devices.forms.createDevice.aeTitleFormatHint')}
                      </p>
                    </div>

                    {/* Manufacturer Field */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-title-page-color-300 dark:text-gray-400 mb-1">
                        {t('devices.forms.createDevice.manufacturer')}
                        <Tooltip content={t('devices.tooltips.aeManufacturer')}>
                          <HelpCircle className="h-4 w-4 text-slate-500 dark:text-gray-500" />
                        </Tooltip>
                      </label>
                      <CreatableSelect
                        value={formData.aeManufacturer}
                        options={MANUFACTURER_OPTIONS}
                        onChange={(value) => {
                          setFormData({
                            ...formData,
                            aeManufacturer: value,
                            aeModel: '', // Reset model when manufacturer changes
                          });
                        }}
                        placeholder={t('devices.forms.createDevice.manufacturerPlaceholderShort')}
                      />
                    </div>

                    {/* Model Field */}
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-title-page-color-300 dark:text-gray-400 mb-1">
                        {t('devices.common.model')}
                        <Tooltip content={t('devices.tooltips.aeModel')}>
                          <HelpCircle className="h-4 w-4 text-slate-500 dark:text-gray-500" />
                        </Tooltip>
                      </label>
                      <CreatableSelect
                        value={formData.aeModel}
                        options={
                          formData.aeManufacturer &&
                          MANUFACTURER_MODEL_DATA[formData.aeManufacturer]
                            ? MANUFACTURER_MODEL_DATA[formData.aeManufacturer]
                            : []
                        }
                        onChange={(value) => {
                          setFormData({
                            ...formData,
                            aeModel: value,
                          });
                        }}
                        placeholder={
                          formData.aeManufacturer
                            ? t('devices.forms.createDevice.modelPlaceholderFor', { manufacturer: formData.aeManufacturer })
                            : t('devices.common.selectManufacturerFirst')
                        }
                        isDisabled={!formData.aeManufacturer}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-title-page-color-300 dark:text-gray-400 mb-1">
                        {t('devices.forms.createDevice.descriptionLabel')}
                      </label>
                      <Textarea
                        placeholder={t('devices.forms.createDevice.aeDescriptionPlaceholder')}
                        className="min-h-[60px]"
                        value={formData.aeDescription}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            aeDescription: (e.target as HTMLTextAreaElement)
                              .value,
                          })
                        }
                      />
                    </div>
                  </div>

                  {formData.aeConfigType === 'auto' && (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-4">
                      <div className="flex items-center gap-2 text-green-800 dark:text-green-400">
                        <Zap className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          {t('devices.forms.createDevice.autoConfigurationSelected')}
                        </span>
                      </div>
                      <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                        {t('devices.forms.createDevice.aeAutoCreatedHint')}
                      </p>
                      <ul className="text-xs text-green-700 dark:text-green-300 mt-2 ml-4 list-disc space-y-1">
                        <li>{t('devices.forms.createDevice.supportedServicesList')}</li>
                        <li>{t('devices.forms.createDevice.supportedServicesList2')}</li>
                        <li>{t('devices.forms.createDevice.supportedServicesList3')}</li>
                        <li>{t('devices.forms.createDevice.supportedServicesList4')}</li>
                        <li>
                          {t('devices.forms.createDevice.transferSyntaxesJpegHint')}
                        </li>
                      </ul>
                    </div>
                  )}

                  {formData.aeConfigType === 'custom' && (
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <h4 className="text-base font-semibold flex items-center gap-2 text-title-page-color-100 dark:text-white border-b border-slate-200 dark:border-gray-700 pb-2">
                          <Settings className="h-4 w-4 text-blue-600" />
                          {t('devices.forms.createAe.abstractSyntaxConfig')}
                        </h4>
                      </div>

                      {/* Abstract Syntax Configurations */}
                      <div className="space-y-4">
                        {Object.entries(abstractSyntaxInfo).map(
                          ([key, info]) => {
                            const abstractKey = key as keyof DicomSyntaxConfig;
                            const config = syntaxConfig[abstractKey];
                            const acceptedCount = getAcceptedCount(abstractKey);
                            const totalCount = Object.keys(config).length;

                            return (
                              <div
                                key={key}
                                className="border border-slate-200 dark:border-gray-700 rounded-lg"
                              >
                                <div
                                  className="p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-gray-700 transition-colors"
                                  onClick={() => toggleAbstractSyntax(key)}
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                      {expandedAbstractSyntax[key] ? (
                                        <ChevronDown className="h-4 w-4 text-slate-500 dark:text-gray-400" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4 text-slate-500 dark:text-gray-400" />
                                      )}
                                      <div>
                                        <h4 className="text-base font-medium text-title-page-color-100 dark:text-white">
                                          {tDicomAbstractName(key)}
                                        </h4>
                                        <p className="text-sm text-title-page-color-300 dark:text-gray-400">
                                          {tDicomAbstractDescription(key)}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge
                                        className={
                                          acceptedCount > 0
                                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                                        }
                                      >
                                        {t('devices.forms.createDevice.acceptedCountBadge', { accepted: acceptedCount, total: totalCount })}
                                      </Badge>
                                    </div>
                                  </div>
                                </div>

                                {expandedAbstractSyntax[key] && (
                                  <div className="px-4 pb-4">
                                    <div className="space-y-4">
                                      <div className="flex items-center justify-between">
                                        <div className="text-sm text-title-page-color-300 dark:text-gray-400">
                                          <strong>{t('devices.common.sopClassUid')}</strong>{' '}
                                          {info.sopClassUid}
                                        </div>
                                        <div className="flex gap-2">
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() =>
                                              bulkUpdateAbstractSyntax(
                                                abstractKey,
                                                'accepted'
                                              )
                                            }
                                            className="text-green-600 border-green-200 hover:bg-green-50 dark:hover:bg-green-900/20"
                                          >
                                            {t('devices.common.acceptAll')}
                                          </Button>
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() =>
                                              bulkUpdateAbstractSyntax(
                                                abstractKey,
                                                'rejected'
                                              )
                                            }
                                            className="text-red-600 border-red-200 hover:bg-red-50 dark:hover:bg-red-900/20"
                                          >
                                            {t('devices.common.rejectAll')}
                                          </Button>
                                        </div>
                                      </div>

                                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-3">
                                        <div className="flex items-center gap-2 text-blue-800 dark:text-blue-400">
                                          <AlertTriangle className="h-4 w-4" />
                                          <span className="text-sm font-medium">
                                            {tDicomAbstractSecurityNote(key)}
                                          </span>
                                        </div>
                                      </div>

                                      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                                        {/* Transfer Syntax Controls */}
                                        <div className="space-y-3">
                                          <h4 className="font-medium text-title-page-color-100 dark:text-white">
                                            {t('devices.common.transferSyntaxSupport')}
                                          </h4>

                                          {/* Uncompressed Transfer Syntaxes */}
                                          <div className="space-y-2">
                                            <h5 className="text-sm font-medium text-title-page-color-300 dark:text-gray-400">
                                              {t('devices.common.uncompressed')}
                                            </h5>
                                            <div className="grid grid-cols-1 gap-2">
                                              {Object.entries(
                                                transferSyntaxInfo
                                              )
                                                .filter(
                                                  ([_, info]) =>
                                                    info.type === 'Uncompressed'
                                                )
                                                .map(
                                                  ([
                                                    transferKey,
                                                    transferInfo,
                                                  ]) => {
                                                    if (
                                                      !(transferKey in config)
                                                    ) {
                                                      return null;
                                                    }
                                                    const value =
                                                      config[
                                                        transferKey as keyof AbstractSyntaxConfig
                                                      ];
                                                    return (
                                                      <div
                                                        key={transferKey}
                                                        className="flex items-center justify-between p-2 rounded border border-slate-200 dark:border-gray-600 bg-card-color-100 dark:bg-gray-800"
                                                      >
                                                        <div className="flex-1">
                                                          <div className="text-sm font-medium text-title-page-color-100 dark:text-white">
                                                            {tDicomTransferSyntaxName(transferKey)}
                                                          </div>
                                                          <div className="text-xs text-title-page-color-300 dark:text-gray-400 font-mono">
                                                            {transferInfo.uid}
                                                          </div>
                                                        </div>
                                                        <Select
                                                          value={value}
                                                          onChange={(e) =>
                                                            updateTransferSyntax(
                                                              abstractKey,
                                                              transferKey,
                                                              e.target
                                                                .value as TransferSyntaxValue
                                                            )
                                                          }
                                                          className="w-24"
                                                        >
                                                          <option
                                                            value="accepted"
                                                            className="text-green-600"
                                                          >
                                                            {t('devices.forms.createDevice.accept')}
                                                          </option>
                                                          <option
                                                            value="rejected"
                                                            className="text-red-600"
                                                          >
                                                            {t('devices.forms.createDevice.reject')}
                                                          </option>
                                                        </Select>
                                                      </div>
                                                    );
                                                  }
                                                )}
                                            </div>
                                          </div>

                                          {/* Compressed Transfer Syntaxes */}
                                          <div className="space-y-2">
                                            <h5 className="text-sm font-medium text-title-page-color-300 dark:text-gray-400">
                                              {t('devices.common.compressed')}
                                            </h5>
                                            <div className="grid grid-cols-1 gap-2">
                                              {Object.entries(
                                                transferSyntaxInfo
                                              )
                                                .filter(
                                                  ([_, info]) =>
                                                    info.type === 'Compressed'
                                                )
                                                .map(
                                                  ([
                                                    transferKey,
                                                    transferInfo,
                                                  ]) => {
                                                    if (
                                                      !(transferKey in config)
                                                    ) {
                                                      return null;
                                                    }
                                                    const value =
                                                      config[
                                                        transferKey as keyof AbstractSyntaxConfig
                                                      ];
                                                    return (
                                                      <div
                                                        key={transferKey}
                                                        className="flex items-center justify-between p-2 rounded border border-slate-200 dark:border-gray-600 bg-card-color-100 dark:bg-gray-800"
                                                      >
                                                        <div className="flex-1">
                                                          <div className="text-sm font-medium text-title-page-color-100 dark:text-white">
                                                            {tDicomTransferSyntaxName(transferKey)}
                                                          </div>
                                                          <div className="text-xs text-title-page-color-300 dark:text-gray-400 font-mono">
                                                            {transferInfo.uid}
                                                          </div>
                                                        </div>
                                                        <Select
                                                          value={value}
                                                          onChange={(e) =>
                                                            updateTransferSyntax(
                                                              abstractKey,
                                                              transferKey,
                                                              e.target
                                                                .value as TransferSyntaxValue
                                                            )
                                                          }
                                                          className="w-24"
                                                        >
                                                          <option
                                                            value="accepted"
                                                            className="text-green-600"
                                                          >
                                                            {t('devices.forms.createDevice.accept')}
                                                          </option>
                                                          <option
                                                            value="rejected"
                                                            className="text-red-600"
                                                          >
                                                            {t('devices.forms.createDevice.reject')}
                                                          </option>
                                                        </Select>
                                                      </div>
                                                    );
                                                  }
                                                )}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          }
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-6">
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                className="min-w-[120px]"
              >
                {t('Cancel')}
              </Button>
            )}
            <Button
              type="submit"
              disabled={isSubmitting}
              className="min-w-[120px] bg-slate-800 hover:bg-slate-900 text-white"
            >
              {isSubmitting ? t('devices.forms.createDevice.creating') : t('devices.forms.createDevice.create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );

  return isModal ? (
    content
  ) : (
    <div className="max-w-4xl mx-auto p-6 bg-gradient-to-br from-slate-50 via-blue-50 to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 min-h-screen">
      {content}
    </div>
  );
}
